import type { MutationFunction, UseQueryResult } from "@tanstack/react-query";
import { usePrefetchQuery, useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import Sentry from "@similarweb/sentry-logger";

import { llmTrackerQueryKeys } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/hooks/useLLMReportQuery";
import type { IAiTrackerCampaign } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";

import type { IAiTrackerCreateCampaignResponse, ICreateCampaignBasePayload } from "./llmTrackerCampignsQueries";
import { campaignMutations, campaignOptions, LLM_TRACKER_CAMPAIGNS_QUERY_KEY } from "./llmTrackerCampignsQueries";

const useInvalidateLLMTrackerCaches = () => {
    const queryClient = useQueryClient();

    const invalidateCampaignsList = (): void => {
        void queryClient.invalidateQueries({ queryKey: [LLM_TRACKER_CAMPAIGNS_QUERY_KEY] });
    };

    const removeAllReportQueriesForCampaign = (campaignId: string): void => {
        void queryClient.removeQueries({
            queryKey: llmTrackerQueryKeys.campaign(campaignId),
        });
    };

    return {
        invalidateCampaignsList,
        invalidateAllDataForCampaign: (campaignId: string): void => {
            invalidateCampaignsList();
            removeAllReportQueriesForCampaign(campaignId);
        },
    };
};

const useGenericCreateCampaign = <T extends ICreateCampaignBasePayload>(
    creationFn: MutationFunction<IAiTrackerCreateCampaignResponse, T>,
) => {
    const { invalidateCampaignsList } = useInvalidateLLMTrackerCaches();

    return useMutation({
        mutationFn: creationFn,
        onSuccess: () => {
            invalidateCampaignsList();
        },
        onError: (error, variables) => {
            Sentry.errorTracker({
                e: error,
                message: "Error creating AI Tracker campaign",
                level: "error",
                params: {
                    extra: {
                        campaignName: variables.campaignName,
                        domains: [variables.primaryDomain, ...(variables.additionalDomains ?? [])].filter(Boolean),
                    },
                },
            });
        },
    });
};

export const useCreateCampaign = () => useGenericCreateCampaign(campaignMutations.create);
export const useCreateCampaignWithPrompts = () => useGenericCreateCampaign(campaignMutations.createWithPrompts);

export const useUpdateCampaign = () => {
    const { invalidateAllDataForCampaign } = useInvalidateLLMTrackerCaches();

    return useMutation({
        mutationFn: campaignMutations.update,
        onSuccess: (_, variables) => {
            invalidateAllDataForCampaign(variables.campaignId);
        },
        onError: (error, variables) => {
            Sentry.errorTracker({
                e: error,
                message: "Error updating AI Tracker campaign",
                level: "error",
                params: {
                    extra: {
                        campaignId: variables.campaignId,
                        campaignName: variables.name,
                    },
                },
            });
        },
    });
};

export const useDeleteCampaign = () => {
    const { invalidateCampaignsList } = useInvalidateLLMTrackerCaches();

    return useMutation({
        mutationFn: campaignMutations.delete,
        onSuccess: () => {
            invalidateCampaignsList();
        },
        onError: (error, campaign) => {
            Sentry.errorTracker({
                e: error,
                message: "Error deleting AI Tracker campaign",
                level: "error",
                params: {
                    extra: {
                        campaignId: campaign.id,
                    },
                },
            });
        },
    });
};

export const useCampaignDetails = () => {
    return useMutation({
        mutationFn: campaignMutations.details,
        onError: (error, campaign) => {
            Sentry.errorTracker({
                e: error,
                message: "Error fetching AI Tracker campaign details",
                level: "error",
                params: {
                    extra: {
                        campaignId: campaign.campaignId,
                    },
                },
            });
        },
    });
};

interface IWeeklyReportSubscriptionParams {
    campaignId: string;
    subscribe: boolean;
}

export const useWeeklyReportSubscription = () => {
    const { invalidateCampaignsList } = useInvalidateLLMTrackerCaches();

    return useMutation({
        mutationFn: ({ campaignId, subscribe }: IWeeklyReportSubscriptionParams) =>
            campaignMutations.subscribeWeeklyReport(campaignId, subscribe),
        onSuccess: () => {
            invalidateCampaignsList();
        },
        onError: (error, variables) => {
            Sentry.errorTracker({
                e: error,
                message: "Error updating weekly report subscription",
                level: "error",
                params: {
                    extra: {
                        campaignId: variables.campaignId,
                        subscribe: variables.subscribe,
                    },
                },
            });
        },
    });
};

export const useLLMTrackerCampaigns = (): UseQueryResult<IAiTrackerCampaign[]> => {
    return useQuery({
        ...campaignOptions.getAll(),
    });
};

export const useLLMTrackerCampaignById = (id: string): UseQueryResult<IAiTrackerCampaign | null> => {
    return useQuery({
        ...campaignOptions.getById(id),
    });
};

export const useLLMTrackerCampaignsPrefetch = () => {
    return usePrefetchQuery({
        ...campaignOptions.getAll(),
    });
};
